export const PHOTOSPHERE_GLSL = `    const float photosphereRadius = 0.3125;
    float angle = atan(centered.y, centered.x);
    float time = u_simulationTime;
    float disk = 1.0 - smoothstep(
      photosphereRadius - 0.008,
      photosphereRadius + 0.004,
      radius
    );
    vec2 surfacePoint = centered / photosphereRadius;
    float sphereZ = sqrt(max(0.0, 1.0 - dot(surfacePoint, surfacePoint)));
    vec3 surfaceNormal = normalize(vec3(surfacePoint, sphereZ));
    float rigidRotation = time * 0.018;
    float rotationCosine = cos(rigidRotation);
    float rotationSine = sin(rigidRotation);
    vec3 rotatingSurface = vec3(
      surfaceNormal.x * rotationCosine + surfaceNormal.z * rotationSine,
      surfaceNormal.y,
      surfaceNormal.z * rotationCosine - surfaceNormal.x * rotationSine
    );
    vec3 broadDrift = vec3(
      sin(time * 0.13),
      cos(time * 0.11),
      sin(time * 0.09 + 1.7)
    ) * 0.72;
    mat3 cellularRotation = mat3(
      0.36, 0.80, 0.48,
      -0.80, 0.00, 0.60,
      0.48, -0.60, 0.64
    );
    vec3 rotatedCells = cellularRotation * rotatingSurface;
    float broadConvection = valueNoise3D(rotatingSurface * 5.2 + broadDrift);
    float crossConvection = valueNoise3D(
      rotatedCells * 7.1 - broadDrift.zxy + 13.7
    );
    vec3 domainWarp = (
      vec3(broadConvection, crossConvection, broadConvection - crossConvection) -
      vec3(0.5, 0.5, 0.0)
    ) * 3.1;
    vec3 granularDrift = vec3(
      sin(time * 0.51),
      cos(time * 0.43),
      sin(time * 0.37 + 2.1)
    ) * 1.18;
    float granulation = valueNoise3D(
      rotatingSurface * 22.0 + domainWarp + granularDrift
    );
    float microGranulation = valueNoise3D(
      rotatedCells * 49.0 - domainWarp * 1.4 - granularDrift * 0.7 + 29.3
    );
    float granuleCore = smoothstep(
      0.36,
      0.76,
      granulation * 0.76 + microGranulation * 0.24
    );
    float granuleBoundary = 1.0 - smoothstep(
      0.27,
      0.49,
      granulation * 0.82 + crossConvection * 0.18
    );
    float faculae = smoothstep(
      0.62,
      0.84,
      broadConvection * 0.34 + microGranulation * 0.66
    );
    vec3 spotCenterA = normalize(vec3(-0.31, 0.17, 0.94));
    vec3 spotCenterB = normalize(vec3(0.29, -0.16, 0.94));
    vec3 spotCenterC = normalize(vec3(-0.21, 0.14, 0.97));
    float spotDistortion = microGranulation - 0.5;
    float spotA = irregularSunspot(rotatingSurface, spotCenterA, 0.078, spotDistortion);
    float spotB = irregularSunspot(rotatingSurface, spotCenterB, 0.058, -spotDistortion);
    float spotC = irregularSunspot(rotatingSurface, spotCenterC, 0.033, spotDistortion);
    float sunspotPenumbra = max(spotA, max(spotB, spotC));
    float sunspotUmbra = max(
      irregularSunspot(rotatingSurface, spotCenterA, 0.034, spotDistortion),
      max(
        irregularSunspot(rotatingSurface, spotCenterB, 0.025, -spotDistortion),
        irregularSunspot(rotatingSurface, spotCenterC, 0.014, spotDistortion)
      )
    );
    sunspotPenumbra *= smoothstep(0.18, 0.62, sphereZ);
    sunspotUmbra *= smoothstep(0.18, 0.62, sphereZ);
    float surfaceHeat = clamp(
      0.38 +
      granuleCore * 0.44 +
      (broadConvection - 0.5) * 0.16 +
      faculae * 0.10,
      0.0,
      1.0
    );
    vec3 photosphere = mix(
      vec3(0.96, 0.30, 0.018),
      vec3(1.0, 0.86, 0.34),
      surfaceHeat
    );
    photosphere *= 1.0 - granuleBoundary * 0.10;
    photosphere *= 0.66 + sphereZ * 0.54;
    photosphere = mix(
      photosphere,
      vec3(0.39, 0.075, 0.018),
      sunspotPenumbra * 0.68
    );
    photosphere = mix(photosphere, vec3(0.11, 0.024, 0.012), sunspotUmbra * 0.74);

    vec2 coronaDirection = centered / max(radius, 0.0001);
    float coronaNoise = valueNoise(
      coronaDirection * 4.3 + vec2(sin(time * 0.17), cos(time * 0.14)) * 0.55
    );
    float primaryRays = pow(
      max(
        0.0,
        0.5 + 0.5 * sin(angle * 23.0 + coronaNoise * 7.8)
      ),
      7.0
    );
    float secondaryRays = pow(
      max(0.0, 0.5 + 0.5 * sin(angle * 41.0 - coronaNoise * 5.3)),
      10.0
    );
    float rayPattern = max(primaryRays, secondaryRays * 0.58);
    float outerCorona =
      smoothstep(photosphereRadius - 0.01, photosphereRadius + 0.018, radius) *
      (1.0 - smoothstep(photosphereRadius + 0.03, 0.50, radius));
    float coronaSignal = outerCorona *
      (0.055 + coronaNoise * 0.13 + rayPattern * 0.24) *
      (0.88 + sin(time * 0.41 + angle * 3.0) * 0.12);

    float flareSignal = max(
      solarEruption(angle, radius, time, 1.3),
      max(
        solarEruption(angle, radius, time, 2.7) * 0.86,
        solarEruption(angle, radius, time, 4.9) * 0.72
      )
    );
    float archedFlare = solarJacobsLadderFlare(angle, radius, time);
    flareSignal = max(flareSignal, archedFlare * 1.12);
    float spicules = pow(max(0.0, sin(angle * 73.0 + coronaNoise * 12.0)), 8.0) *
      (1.0 - smoothstep(photosphereRadius + 0.006, photosphereRadius + 0.036, radius)) *
      smoothstep(photosphereRadius - 0.006, photosphereRadius + 0.004, radius);
    float chromosphere =
      1.0 - smoothstep(0.003, 0.014, abs(radius - photosphereRadius));
    vec3 premultipliedRadiance =
      photosphere * disk * 1.22 +
      vec3(1.0, 0.24, 0.018) * chromosphere * 0.72 +
      vec3(1.0, 0.30, 0.035) * coronaSignal +
      vec3(1.0, 0.50, 0.075) * (flareSignal * 1.18 + spicules * 0.42);
    alpha = max(
      disk,
      clamp(coronaSignal + flareSignal + spicules + chromosphere * 0.76, 0.0, 0.94)
    );
    color = premultipliedRadiance / max(alpha, 0.001);
`;
